/**
 * StoragePositionCell
 *
 * A single cell inside a shelf grid. Represents one storage position and
 * renders its visual state based on backend data + any local pending change.
 *
 * Props:
 *   pos            – Position object from shelveStoragePositionsManager endpoint.
 *                    Key fields: { id, shelve, position, depth, resources[], topResource }
 *                    resources[] order: [topResource (Part/Tool/RM), Fixture (base)]
 *   pendingChange  – Object or undefined. Shape: { action, resource?, linkedResourceId?, … }
 *                    Supported actions:
 *                      'set'     – resource being placed (not yet saved)
 *                      'remove'  – entire position being cleared
 *                      'unmount' – only the top resource is being removed (Fixture stays)
 *   isSelecting    – true when a resource is selected in the left panel (shows accent + on empty cells)
 *   onClick        – (event, pos) => void  — bubbles up to WarehouseStorageManager.handleCellClick
 *
 * Visual states:
 *   - Empty / no pending  : dashed border, transparent bg, "+" icon
 *   - Saved occupied       : solid vivid border+bg matching resource type colour
 *   - Pending set          : same colour but muted bg + orange dashed outline + dot
 *   - Stacked (×N)        : "×N" badge top-right
 */
import { Box, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { AddRounded, CategoryRounded, Grid3x3Rounded, HandymanRounded, ScatterPlotRounded } from '@mui/icons-material'
import { TYPE_META, PENDING_COLOR, styles } from './warehouseStorageStyles'
import { STATUS_COLORS } from './StatusFilterChips'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { systemValueCodeLabel } from '../generic/systemValues/systemValueLabel'

function getEffectiveResources (pos, pendingChange) {
  if (pendingChange) {
    if (pendingChange.action === 'remove') return []
    if (pendingChange.action === 'set') {
      const savedFixture = (pos.resources || []).find(r => (r.typeKey || r.type?.name || '') === 'Fixture')
      const resTypeKey = pendingChange.resource?.typeKey || pendingChange.resource?.type?.name || ''
      if (savedFixture && resTypeKey !== 'Fixture') {
        return [pendingChange.resource, savedFixture]
      }
      if (!savedFixture && resTypeKey !== 'Fixture' && pendingChange.resource?.fixture) {
        return [pendingChange.resource, { ...pendingChange.resource.fixture, typeKey: 'Fixture' }]
      }
      return [pendingChange.resource]
    }
    if (pendingChange.action === 'unmount') return (pos.resources || []).slice(1)
    if (pendingChange.action === 'setWithFixture') return [pendingChange.resource, pendingChange.fixture]
  }
  return pos.resources || []
}

export default function StoragePositionCell ({ pos, pendingChange, isSelecting, compatibilityState, onClick }) {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const accent = theme.palette.primary.main

  const effectiveResources = getEffectiveResources(pos, pendingChange)
  const isEmpty = effectiveResources.length === 0
  const isStacked = effectiveResources.length > 1
  const topResource = effectiveResources[0]
  const isPending = !!pendingChange
  // Normalise: backend may return "Raw Material" (with space) → "RawMaterial"
  const resolvedTypeKey = (topResource?.typeKey || topResource?.type?.name || '').replace(/\s+/g, '')
  const typeColor = isEmpty ? null : (TYPE_META[resolvedTypeKey]?.color || '#9e9e9e')

  // Saved: vivid colour matching the left panel. Pending: muted + orange indicator.
  const savedBg = typeColor ? alpha(typeColor, 0.22) : 'transparent'
  const pendingBg = typeColor ? alpha(typeColor, 0.08) : 'transparent'
  const savedShadow = typeColor ? `0 0 12px ${alpha(typeColor, 0.3)}, 0 2px 6px rgba(0,0,0,0.4)` : 'none'
  const pendingShadow = '0 2px 6px rgba(0,0,0,0.3)'

  // Compatibility-aware styles (only applied to empty cells when a resource is selected)
  const isIncompatible = compatibilityState === 'incompatible'

  return (
    <Box
      onClick={(e) => onClick(e, pos)}
      sx={{
        width: '100%',
        minHeight: 64,
        border: '2px solid',
        borderColor: isIncompatible
          ? 'rgba(255,82,82,0.35)'
          : isEmpty
            ? (isSelecting ? alpha(accent, 0.4) : theme.palette.background.default)
            : isPending ? alpha(typeColor, 0.5) : typeColor,
        borderStyle: isEmpty ? 'dashed' : 'solid',
        opacity: isIncompatible ? 0.4 : 1,
        borderRadius: 1.5,
        cursor: isIncompatible ? 'not-allowed' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isEmpty ? 'transparent' : (isPending ? pendingBg : savedBg),
        outline: isPending && !isEmpty
          ? `2px dashed ${PENDING_COLOR}`
          : (!isEmpty && compatibilityState === 'compatible')
              ? `2px solid ${alpha(accent, 0.45)}`
              : 'none',
        outlineOffset: '3px',
        position: 'relative',
        userSelect: 'none',
        boxShadow: isEmpty
          ? 'inset 0 0 0 1px rgba(255,255,255,0.03)'
          : isPending
            ? pendingShadow
            : compatibilityState === 'compatible'
              ? `${savedShadow}, 0 0 10px ${alpha(accent, 0.3)}`
              : savedShadow,
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: accent,
          bgcolor: isEmpty ? alpha(accent, 0.05) : alpha(typeColor, 0.28),
          transform: 'scale(1.05)',
          zIndex: 2,
          boxShadow: `0 4px 16px ${alpha(accent, 0.15)}`
        }
      }}
    >
      {isStacked && <Box sx={styles.stackBadge}>×{effectiveResources.length}</Box>}
      {isPending && !isEmpty && <Box sx={styles.pendingDot} />}
      {isEmpty
        ? (
          <AddRounded sx={{
            fontSize: 30,
            color: isSelecting ? alpha(accent, 0.4) : theme.palette.background.default,
            transition: 'color 0.2s'
          }}
          />
          )
        : (
          <>
            <Box sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              bgcolor: isPending ? alpha(typeColor, 0.15) : alpha(typeColor, 0.3),
              border: `2px solid ${typeColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 0.4,
              opacity: isPending ? 0.7 : 1
            }}
            >
              {resolvedTypeKey === 'Fixture' && <Grid3x3Rounded sx={{ fontSize: 16, color: typeColor }} />}
              {resolvedTypeKey === 'Tool' && <HandymanRounded sx={{ fontSize: 16, color: typeColor }} />}
              {resolvedTypeKey === 'RawMaterial' && <ScatterPlotRounded sx={{ fontSize: 16, color: typeColor }} />}
              {resolvedTypeKey !== 'Fixture' && resolvedTypeKey !== 'Tool' && resolvedTypeKey !== 'RawMaterial' && <CategoryRounded sx={{ fontSize: 16, color: typeColor }} />}
            </Box>
            <Typography sx={{
              fontSize: '0.72rem',
              color: isPending ? '#888' : '#ddd',
              maxWidth: '90%',
              ...styles.truncated,
              textAlign: 'center',
              lineHeight: 1.2,
              px: 0.5
            }}
            >
              {topResource?.code || topResource?.name || '—'}
            </Typography>
            {resolvedTypeKey !== 'Fixture' && resolvedTypeKey !== 'Tool' && resolvedTypeKey !== 'RawMaterial' && topResource?.status && (
              <Box sx={{
                mt: 0.4,
                px: 0.6,
                py: 0.2,
                borderRadius: 0.6,
                bgcolor: alpha(STATUS_COLORS[topResource.status] || '#9e9e9e', 0.15),
                border: `1px solid ${alpha(STATUS_COLORS[topResource.status] || '#9e9e9e', 0.4)}`
              }}
              >
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: STATUS_COLORS[topResource.status] || '#9e9e9e', letterSpacing: '0.03em', lineHeight: 1.4 }}>
                  {systemValueCodeLabel(t_, 'PartStatusEnum', topResource.status)}
                </Typography>
              </Box>
            )}
          </>
          )}
    </Box>
  )
}
